import { NetworkError, type YomiriHttpClient } from "../network/types.ts";
import { validateProfile } from "./ProfileValidator.ts";
import { buildRequest } from "./RequestBuilder.ts";
import { parseHtml, htmlField, extractHtml } from "./HtmlExtractor.ts";
import { parseJson, jsonField, extractJson } from "./JsonExtractor.ts";
import { decode } from "./decoders/registry.ts";
import { resolveUrl } from "./UrlResolver.ts";
import {
  ExtractionError,
  SourceEngineError,
  type SourceProfile,
  type Operation,
  type OperationContext,
  type OperationResult,
  type RecordValue,
  type Value,
  type SourceStep,
} from "./types.ts";

export class SourceEngine {
  private readonly profile: SourceProfile;
  constructor(
    profile: unknown,
    private readonly http: Pick<YomiriHttpClient, "text">,
  ) {
    this.profile = validateProfile(profile);
  }
  search(context: OperationContext = {}) {
    return this.run("search", context);
  }
  detail(context: OperationContext) {
    return this.run("detail", context);
  }
  chapters(context: OperationContext) {
    return this.run("chapters", context);
  }
  pages(context: OperationContext) {
    return this.run("pages", context);
  }
  async run(operation: Operation, context: OperationContext): Promise<OperationResult> {
    const definition = this.profile.operations[operation];
    const variants = [definition.steps, ...(definition.fallbacks ?? []).map((f) => f.steps)];
    for (const [index, steps] of variants.entries()) {
      try {
        return await this.runSteps(operation, context, steps);
      } catch (error) {
        if (
          !(error instanceof SourceEngineError) ||
          index === variants.length - 1 ||
          !(
            error.diagnostic.code === "SELECTOR_MISMATCH" ||
            (error.diagnostic.code === "HTTP_ERROR" && error.diagnostic.status === 404)
          )
        )
          throw error;
      }
    }
    throw new Error("Operación sin pasos.");
  }
  private async runSteps(
    operation: Operation,
    context: OperationContext,
    steps: SourceStep[],
  ): Promise<OperationResult> {
    let prev: RecordValue = {},
      finalUrl = this.profile.baseUrl,
      items: RecordValue[] = [];
    for (const [stepIndex, step] of steps.entries()) {
      let url = step.url,
        status: number | undefined;
      try {
        if (context.signal?.aborted) throw new NetworkError("ABORTED", "Solicitud cancelada.");
        const request = buildRequest(this.profile, step, context, prev, finalUrl);
        url = request.url;
        const response = await this.http.text(request);
        if (context.signal?.aborted) throw new NetworkError("ABORTED", "Solicitud cancelada.");
        status = response.status;
        finalUrl = resolveUrl(response.finalUrl, url, this.profile);
        url = finalUrl;
        if (response.classification === "challenge")
          throw new NetworkError("CHALLENGE_REQUIRED", "El sitio requiere verificación.", status);
        if (status < 200 || status >= 300)
          throw new NetworkError("HTTP_ERROR", `HTTP ${status}`, status);
        let doc: Document | undefined, json: Value | undefined;
        const html = () => (doc ??= parseHtml(response.body));
        const data = () => (json ??= parseJson(response.body));
        const captured: RecordValue = {};
        for (const [key, field] of Object.entries(step.capture ?? {}))
          captured[key] =
            field.path !== undefined
              ? jsonField(data(), field, finalUrl, this.profile)
              : htmlField(html(), field, finalUrl, this.profile);
        prev = captured;
        if (typeof prev.nextUrl === "string")
          prev.nextUrl = resolveUrl(prev.nextUrl, finalUrl, this.profile);
        if (step.extract) {
          const rule = step.extract;
          if (rule.kind === "html") items = extractHtml(html(), rule, finalUrl, this.profile);
          else {
            const root = rule.decoder
              ? decode(
                  rule.decoder,
                  rule.selector
                    ? String(htmlField(html(), { selector: rule.selector }, finalUrl, this.profile))
                    : response.body,
                )
              : data();
            items = extractJson(root, rule, finalUrl, this.profile);
          }
        }
      } catch (error) {
        throw new SourceEngineError(
          {
            operation,
            stepIndex,
            url,
            status: status ?? (error instanceof NetworkError ? error.status : undefined),
            code:
              error instanceof NetworkError || error instanceof ExtractionError
                ? error.code
                : "INVALID_RESPONSE",
            message: error instanceof Error ? error.message : "Falló la operación.",
          },
          error,
        );
      }
    }
    return {
      items,
      finalUrl,
      ...(typeof prev.nextUrl === "string" ? { nextUrl: prev.nextUrl } : {}),
    };
  }
}
